/**
 * EmailVerificationScreen — the "Check your email" UI after profile pick.
 *
 * Shown on the device that initiated the sign-in (the one that called
 * requestEmailLogin). The user can either:
 *
 *   1. Click the magic link in their email — same device → auto sign-in
 *      handled by /auth/verify route, this screen never sees it.
 *   2. Type the 6-digit code from the email into this screen — code
 *      submitted via verifyEmailLoginCode → custom Firebase Auth token →
 *      sign in.
 *   3. Cross-device click of the link sends the user back here to type
 *      the code on their original device.
 *
 * This component is pure presentation + local state. Phase 4.2 wires the
 * actual Cloud Function callers into the parent (LandingPage), which
 * passes onVerifyCode / onResend callbacks down.
 *
 * Design language matches LandingPage:
 *   - force-dark scope (dark mode regardless of user theme preference)
 *   - brand-dark background with glassmorphism card
 *   - brand-secondary gold accents
 *   - Apple-like 6-box OTP input with auto-focus advance
 *
 * @see Phase 3.2 verifyEmailLoginCode Cloud Function for the server side
 * @see Phase 4.2 LandingPage integration for how this gets mounted
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Loader, Mail, MailCheck, RefreshCw } from 'lucide-react';

export interface EmailVerificationScreenProps {
  /** Display name shown in the "Hi Reuben" greeting. */
  userName: string;

  /** Optional avatar URL. Falls back to an initial-bubble. */
  userAvatar?: string;

  /** Masked email like "r***n@thetechery.com.au" — comes from the
   *  requestEmailLogin response. */
  maskedEmail: string;

  /** ISO timestamp when the code expires. Drives the countdown timer
   *  and disables the input when expiry passes. */
  expiresAt: string;

  /**
   * Called when the user submits a 6-digit code. The parent runs the
   * actual verifyEmailLoginCode Cloud Function. Throws an Error whose
   * .message is user-facing (parent maps CF error codes to friendly
   * strings before throwing). Resolving means sign-in succeeded —
   * parent is responsible for navigating away.
   */
  onVerifyCode: (code: string) => Promise<void>;

  /**
   * Called when the user clicks "Resend email". Parent re-runs
   * requestEmailLogin and returns the new masked email + expiry so this
   * screen can refresh its countdown. Throws an Error on rate-limit /
   * other failure with a user-facing .message.
   */
  onResend: () => Promise<{ maskedEmail: string; expiresAt: string }>;

  /** Called when the user clicks "Back to user selection". Parent
   *  returns to the profile picker. */
  onBack: () => void;
}

/** Seconds to lock the resend button after a successful resend. The
 *  server enforces 3/hr per user, so this is a UX guard against
 *  hammer-clicking — friendlier than letting the user blow their
 *  quota and seeing rate-limit errors. */
const RESEND_COOLDOWN_SECONDS = 30;

/** Number of digit boxes. 6 matches the code length generated by
 *  generateLoginCode in the Cloud Function. */
const CODE_LENGTH = 6;

/**
 * Height of the *visible* viewport, in px — i.e. what's left after the
 * software keyboard takes its share.
 *
 * Why this exists: this screen is a `position: fixed; inset: 0` overlay. On
 * iOS the software keyboard does NOT shrink the layout viewport, so a fixed
 * full-height container keeps its full height and the keyboard simply covers
 * the bottom of it. Combined with the `overflow-hidden` this container used
 * to carry, the code boxes could end up underneath the keyboard with no way
 * to scroll to them — the user sees a keyboard, taps, and nothing appears to
 * happen. Android Chrome resizes the viewport instead, which is why this
 * never reproduced on a Pixel.
 *
 * Binding the container to visualViewport.height makes it genuinely shorter
 * when the keyboard is up, so it overflows, becomes scrollable, and WebKit's
 * native scroll-focused-input-into-view can do its job.
 *
 * Returns null where visualViewport isn't available — callers fall back to
 * plain CSS viewport height.
 */
function useVisualViewportHeight(): number | null {
  const [height, setHeight] = useState<number | null>(() =>
    typeof window !== 'undefined' && window.visualViewport
      ? window.visualViewport.height
      : null
  );

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
    if (!vv) return;
    const onResize = () => setHeight(vv.height);
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, []);

  return height;
}

export function EmailVerificationScreen({
  userName,
  userAvatar,
  maskedEmail,
  expiresAt,
  onVerifyCode,
  onResend,
  onBack,
}: EmailVerificationScreenProps) {
  // ── Local state ──────────────────────────────────────────────────────
  // The code is held as an array so each box maps to a stable index.
  // Joining gives the full 6-digit string when we submit.
  const [code, setCode] = useState<string[]>(() => Array(CODE_LENGTH).fill(''));

  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');

  // Resend cooldown ticks down per second after a successful resend so
  // the user can't spam the button while Resend is still processing.
  const [resendCooldown, setResendCooldown] = useState(0);

  // We track maskedEmail + expiresAt locally so a successful resend can
  // update them without forcing the parent to re-render.
  const [currentMaskedEmail, setCurrentMaskedEmail] = useState(maskedEmail);
  const [currentExpiresAt, setCurrentExpiresAt] = useState(expiresAt);

  // Countdown timer — recomputed every second from currentExpiresAt.
  const [remainingMs, setRemainingMs] = useState(() =>
    Math.max(0, new Date(expiresAt).getTime() - Date.now())
  );

  const viewportHeight = useVisualViewportHeight();

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  // Prevents double-submit when the auto-submit effect fires while a
  // submission is already in flight (e.g. paste a 6-digit code, effect
  // fires, then the user hits Enter before the response lands).
  const submittedCodeRef = useRef<string>('');

  // ── Effects ──────────────────────────────────────────────────────────

  // Auto-focus the first box on mount — but ONLY on devices with a
  // precise pointer (desktop mice + trackpads). On mobile/tablet the
  // focus() call pops the keyboard immediately, which is jarring when
  // the user just hit a button to get here.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(pointer: fine)').matches) {
      inputRefs.current[0]?.focus();
    }
  }, []);

  // Sync local state if the parent passes different props (e.g. user
  // navigated away and back). Cheap; only runs when props change.
  useEffect(() => {
    setCurrentMaskedEmail(maskedEmail);
  }, [maskedEmail]);

  useEffect(() => {
    setCurrentExpiresAt(expiresAt);
    setRemainingMs(Math.max(0, new Date(expiresAt).getTime() - Date.now()));
  }, [expiresAt]);

  // 1Hz countdown tick. Stops automatically once the code expires (no
  // need to keep recomputing). The setInterval is cheap enough that
  // we don't bother throttling it.
  useEffect(() => {
    const interval = setInterval(() => {
      const next = Math.max(0, new Date(currentExpiresAt).getTime() - Date.now());
      setRemainingMs(next);
      if (next === 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [currentExpiresAt]);

  // Resend cooldown tick. Stops at 0.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const isExpired = remainingMs <= 0;

  // ── Submit handler ───────────────────────────────────────────────────
  // Wrapped in useCallback so the auto-submit effect below has a stable
  // identity to depend on.
  const handleSubmit = useCallback(
    async (codeStr: string) => {
      if (codeStr.length !== CODE_LENGTH) return;
      if (submittedCodeRef.current === codeStr) return; // already submitting
      submittedCodeRef.current = codeStr;

      setIsVerifying(true);
      setError('');
      try {
        await onVerifyCode(codeStr);
        // Parent navigates on success — no further action needed.
      } catch (e: unknown) {
        const err = e as { message?: string };
        setError(err?.message || 'Verification failed. Try again.');
        // Reset code + refocus so the user can re-enter.
        setCode(Array(CODE_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
        submittedCodeRef.current = '';
      } finally {
        setIsVerifying(false);
      }
    },
    [onVerifyCode]
  );

  // Auto-submit once all 6 boxes are filled. Mirrors the existing PIN
  // flow's auto-submit-on-4-digits pattern from TechAuthGuard / LandingPage.
  // Note this is gated on isVerifying but NOT on isExpired — see the
  // inputsDisabled comment below for why the client clock doesn't get a veto.
  useEffect(() => {
    const joined = code.join('');
    if (joined.length === CODE_LENGTH && !isVerifying) {
      void handleSubmit(joined);
    }
  }, [code, handleSubmit, isVerifying]);

  // ── Input handlers ───────────────────────────────────────────────────

  const handleChange = (idx: number, raw: string) => {
    // Accept only digits; trim to single char so paste with extra noise
    // doesn't overflow this box (paste handler below handles multi-char
    // pastes).
    const digit = raw.replace(/\D/g, '').slice(-1);
    setCode((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    setError('');
    if (digit && idx < CODE_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace on empty box → focus the previous box so the user can
    // delete its content without manually clicking backwards.
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      e.preventDefault();
      inputRefs.current[idx - 1]?.focus();
      return;
    }
    // Left/right arrows for keyboard navigation between boxes.
    if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      inputRefs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < CODE_LENGTH - 1) {
      e.preventDefault();
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setCode(next);
    setError('');
    const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  // ── Resend handler ───────────────────────────────────────────────────

  const handleResend = async () => {
    setIsResending(true);
    setError('');
    try {
      const result = await onResend();
      setCurrentMaskedEmail(result.maskedEmail);
      setCurrentExpiresAt(result.expiresAt);
      setRemainingMs(Math.max(0, new Date(result.expiresAt).getTime() - Date.now()));
      setCode(Array(CODE_LENGTH).fill(''));
      submittedCodeRef.current = '';
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      inputRefs.current[0]?.focus();
    } catch (e: unknown) {
      const err = e as { message?: string };
      setError(err?.message || "Couldn't send the email. Try again.");
    } finally {
      setIsResending(false);
    }
  };

  // ── Rendering ────────────────────────────────────────────────────────

  const initial = userName.trim().charAt(0).toUpperCase() || '?';

  // Expiry deliberately does NOT disable the inputs. isExpired is derived
  // from the *client* clock against a server-issued timestamp, so a phone
  // running even ten minutes fast renders all six boxes disabled the instant
  // this screen mounts — and "Send a new code" produces another code that is
  // also instantly "expired", leaving no way in at all. The countdown copy
  // still tells the user where they stand; the server is the authority on
  // whether a code is actually stale and says so ("No active sign-in code").
  const inputsDisabled = isVerifying;

  const joinedCode = code.join('');
  const canSubmit = joinedCode.length === CODE_LENGTH && !isVerifying;

  return (
    <div
      className="force-dark fixed inset-0 z-[150] bg-brand-dark flex flex-col items-center px-6 py-8 text-white overflow-y-auto overscroll-contain"
      // Explicit height wins over inset-0's implied bottom:0 (an
      // over-constrained fixed box drops `bottom`), so this shrinks to the
      // keyboard-visible area on iOS and falls back to full height when
      // visualViewport isn't available. See useVisualViewportHeight.
      style={viewportHeight ? { height: `${viewportHeight}px` } : undefined}
    >
      {/* Ambient background orbs — same vibe as LandingPage. `fixed` rather
          than `absolute` so they stay put instead of scrolling away once the
          keyboard turns this container into a scrollable box. */}
      <div className="pointer-events-none fixed -top-32 -left-32 w-96 h-96 rounded-full bg-brand-secondary/[0.05] blur-3xl" />
      <div className="pointer-events-none fixed -bottom-32 -right-32 w-96 h-96 rounded-full bg-blue-500/[0.04] blur-3xl" />

      {/* my-auto centres the card when there's room and simply stops centring
          when there isn't. Plain justify-center would push the top of the
          card off the scroll origin and put it permanently out of reach the
          moment the keyboard makes the card taller than the container. */}
      <div className="relative w-full max-w-md my-auto">
        {/* Glassmorphism card */}
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] rounded-3xl shadow-2xl p-8">
          {/* Avatar + name */}
          <div className="flex flex-col items-center mb-6">
            {userAvatar ? (
              <img
                src={userAvatar}
                alt={userName}
                className="w-16 h-16 rounded-full object-cover border border-white/10 mb-3"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-secondary/30 to-brand-secondary/10 border border-brand-secondary/30 flex items-center justify-center text-2xl font-semibold text-brand-secondary mb-3">
                {initial}
              </div>
            )}
            <p className="text-sm text-white/60">Signing in as</p>
            <p className="text-base font-semibold text-white">{userName}</p>
          </div>

          {/* Headline */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brand-secondary/15 border border-brand-secondary/30 mb-3">
              <Mail className="w-5 h-5 text-brand-secondary" />
            </div>
            <h1 className="text-2xl font-bold text-white">Check your email</h1>
            <p className="text-sm text-white/60 mt-2">
              We sent a sign-in link to
            </p>
            <p className="text-sm font-medium text-white mt-0.5">
              {currentMaskedEmail}
            </p>
            <p className="text-xs text-white/40 mt-2">
              {isExpired
                ? 'Code expired — resend a new one below'
                : `Expires in ${formatCountdown(remainingMs)}`}
            </p>
          </div>

          {/* 6-digit code input */}
          <div
            className="flex justify-center gap-2 sm:gap-3 mb-4"
            onPaste={(e) => handlePaste(e)}
          >
            {Array.from({ length: CODE_LENGTH }).map((_, idx) => (
              <input
                key={idx}
                ref={(el) => {
                  inputRefs.current[idx] = el;
                }}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                autoComplete="one-time-code"
                maxLength={1}
                value={code[idx]}
                disabled={inputsDisabled}
                onChange={(e) => handleChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                onPaste={handlePaste}
                aria-label={`Digit ${idx + 1} of ${CODE_LENGTH}`}
                className={`
                  w-11 h-14 sm:w-12 sm:h-16
                  text-center text-2xl font-mono font-semibold
                  bg-white/[0.06] border rounded-xl text-white
                  transition-all duration-200
                  ${
                    code[idx]
                      ? 'border-brand-secondary/60'
                      : 'border-white/[0.12]'
                  }
                  focus:border-brand-secondary focus:ring-2 focus:ring-brand-secondary/30
                  focus:bg-white/[0.08]
                  disabled:opacity-40 disabled:cursor-not-allowed
                  outline-none
                `}
              />
            ))}
          </div>

          {/* Status / error line */}
          <div className="min-h-[24px] mb-4 text-center">
            {isVerifying && (
              <p className="text-sm text-white/70 inline-flex items-center gap-2">
                <Loader className="w-3.5 h-3.5 animate-spin" />
                Verifying…
              </p>
            )}
            {!isVerifying && error && (
              <p className="text-sm text-rose-300">{error}</p>
            )}
          </div>

          {/* Explicit submit. The boxes still auto-submit when all six fill,
              but that used to be the ONLY way to submit — so any input the
              boxes didn't absorb cleanly (an autofill dropping the whole code
              into one box, a mistyped digit on a box hidden behind the
              keyboard) left the user with a filled-looking form, no button,
              and nothing happening. */}
          <button
            type="button"
            onClick={() => void handleSubmit(joinedCode)}
            disabled={!canSubmit}
            className="w-full py-3 mb-5 rounded-2xl bg-brand-secondary text-brand-dark font-bold text-sm hover:brightness-110 disabled:bg-white/[0.08] disabled:text-white/30 disabled:cursor-not-allowed transition-all"
          >
            {isVerifying ? 'Verifying…' : 'Verify code'}
          </button>

          {/* Helper copy. Leads with the code because that's the path that
              always works — on a phone, the emailed link usually opens in a
              different browser than this one and lands on the "open it where
              you started" screen instead of signing the user in. */}
          <p className="text-xs text-white/40 text-center mb-5 px-2 leading-relaxed">
            Type the 6-digit code from your email above. The link in the email
            works too, as long as it opens in this same browser.
          </p>

          {/* Resend + Back */}
          <div className="flex flex-col items-center gap-3 pt-4 border-t border-white/[0.06]">
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending || resendCooldown > 0 || isVerifying}
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-secondary hover:text-brand-secondary/80 disabled:text-white/30 disabled:cursor-not-allowed transition-colors"
            >
              {isResending ? (
                <>
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                  Sending…
                </>
              ) : resendCooldown > 0 ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5" />
                  Resend in {resendCooldown}s
                </>
              ) : isExpired ? (
                <>
                  <MailCheck className="w-3.5 h-3.5" />
                  Send a new code
                </>
              ) : (
                <>
                  <RefreshCw className="w-3.5 h-3.5" />
                  Resend email
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onBack}
              disabled={isVerifying}
              className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 disabled:text-white/20 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to user selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Format milliseconds as "M:SS" countdown. Floors to seconds so the
 * display ticks down predictably (vs. round which can show 0:60 → 0:59
 * → 0:58 with a brief 1:00 reappearance).
 */
function formatCountdown(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
